import React, { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Alert,
  Fab,
  Badge,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Add,
  FilterList,
  MoreVert,
  Receipt,
  TrendingUp,
  AttachMoney,
  Category,
  CalendarToday,
  Edit,
  Delete,
  Refresh,
  FileDownload,
  Notifications,
  Help as HelpIcon,
  Tour as TourIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { SidebarLayout } from '@/components/Layout'
import { ExpensesService } from '@/utils/expenses-service'
import { ExpenseNotifications, ExpenseNotificationHistory, ExpenseForm } from '@/components/Expenses'
import ExpenseHelpDialog from '@/components/Expenses/ExpenseHelpDialog'
import ExpenseFeatureTour, { useExpenseFeatureTour } from '@/components/Expenses/ExpenseFeatureTour'
import {
  Expense,
  ExpenseCategory,
  ExpenseFilters,
  ExpenseListResponse,
  ExpenseCategoriesResponse,
  ExpenseReminderNotification,
  ExpenseSummary,
  Listing,
  ExpenseForm as ExpenseFormType,
  CreateExpenseRequest
} from '@/types'
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns'

export default function ExpenseManagement() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // State management
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [listings, setListings] = useState<Listing[]>([])
  const [reminders, setReminders] = useState<ExpenseReminderNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  // Filter state
  const [filters, setFilters] = useState<ExpenseFilters>({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  })
  const [showFilters, setShowFilters] = useState(false)

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedExpense, setSelectedExpense] = useState<string | null>(null)

  // Tab state
  const [currentTab, setCurrentTab] = useState(0)

  // Help system state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)
  const { tourOpen, tourCompleted, startTour, closeTour, completeTour } = useExpenseFeatureTour()

  // Expense form state
  const [expenseFormOpen, setExpenseFormOpen] = useState(false)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [savingExpense, setSavingExpense] = useState(false)

  // Summary data
  const [summaryData, setSummaryData] = useState<ExpenseSummary>({
    currentMonth: {
      total: 0,
      count: 0,
      trend: 0,
    },
    recurringExpenses: {
      active: 0,
      total: 0,
    },
    pendingReminders: {
      count: 0,
    },
    overdueReminders: {
      count: 0,
    },
    topCategories: [],
  })


  const loadExpenseData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Load all data in parallel, with fallbacks for optional data
      const [expensesResponse, categoriesResponse, summaryResponse, remindersResponse] = await Promise.allSettled([
        ExpensesService.getExpenses(filters),
        ExpensesService.getExpenseCategories(),
        ExpensesService.getExpenseSummary().catch(() => ({
          currentMonth: { total: 0, count: 0, trend: 0 },
          recurringExpenses: { active: 0, total: 0 },
          pendingReminders: { count: 0 },
          overdueReminders: { count: 0 },
          topCategories: []
        })),
        ExpensesService.getExpenseReminders().catch(() => []),
      ])

      // Handle expenses response
      if (expensesResponse.status === 'fulfilled') {
        setExpenses(expensesResponse.value.expenses)
      } else {
        console.error('Failed to load expenses:', expensesResponse.reason)
        setError('Failed to load expenses. Please try again.')
      }

      // Handle categories response
      if (categoriesResponse.status === 'fulfilled') {
        setCategories([...categoriesResponse.value.predefinedCategories, ...categoriesResponse.value.customCategories])
      } else {
        console.error('Failed to load categories:', categoriesResponse.reason)
        setError('Failed to load categories. Please try again.')
      }

      // Handle summary response (optional)
      if (summaryResponse.status === 'fulfilled') {
        setSummaryData(summaryResponse.value)
      } else {
        console.warn('Summary data not available, using defaults')
        // Keep the default summary data initialized in state
      }

      // Handle reminders response (optional)
      if (remindersResponse.status === 'fulfilled') {
        setReminders(remindersResponse.value)
      } else {
        console.warn('Reminders data not available, using empty array')
        setReminders([])
      }

    } catch (err) {
      console.error('Error loading expense data:', err)
      setError('Failed to load expense data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [filters]);
  useEffect(() => {
    loadExpenseData()
  }, [filters, loadExpenseData])

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadExpenseData()
    setRefreshing(false)
  }

  const handleFilterChange = (field: keyof ExpenseFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expenseId: string) => {
    setAnchorEl(event.currentTarget)
    setSelectedExpense(expenseId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedExpense(null)
  }

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await ExpensesService.deleteExpense(expenseId)
      await loadExpenseData() // Refresh data
      handleMenuClose()
    } catch (err) {
      console.error('Error deleting expense:', err)
      setError('Failed to delete expense. Please try again.')
    }
  }

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.categoryId === categoryId)
    return category?.name || 'Unknown Category'
  }

  const getRecurrenceTypeColor = (type: string) => {
    switch (type) {
      case 'one_time': return 'default'
      case 'fixed_recurring': return 'primary'
      case 'variable_recurring': return 'secondary'
      default: return 'default'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return ExpensesService.formatCurrency(amount, currency)
  }

  const handleAddExpense = () => {
    setEditingExpense(null)
    setExpenseFormOpen(true)
  }

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense)
    setExpenseFormOpen(true)
    handleMenuClose()
  }

  const handleSaveExpense = async (expenseData: ExpenseFormType) => {
    try {
      setSavingExpense(true)
      setError(null)

      const requestData: CreateExpenseRequest = {
        categoryId: expenseData.categoryId,
        amount: expenseData.amount,
        currency: expenseData.currency,
        description: expenseData.description,
        date: expenseData.date,
        receiptUrl: expenseData.receiptUrl,
        listingId: expenseData.listingId,
        recurrenceType: expenseData.recurrenceType,
        recurrenceConfig: expenseData.recurrenceConfig,
      }

      if (editingExpense) {
        // Update existing expense
        await ExpensesService.updateExpense(editingExpense.expenseId, requestData)
      } else {
        // Create new expense
        await ExpensesService.createExpense(requestData)
      }

      // Close form and refresh data
      setExpenseFormOpen(false)
      setEditingExpense(null)
      await loadExpenseData()
    } catch (err) {
      console.error('Error saving expense:', err)
      setError(`Failed to ${editingExpense ? 'update' : 'create'} expense. Please try again.`)
    } finally {
      setSavingExpense(false)
    }
  }

  const handleCancelExpenseForm = () => {
    setExpenseFormOpen(false)
    setEditingExpense(null)
  }

  if (loading) {
    return (
      <SidebarLayout>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>

        <Skeleton variant="rectangular" height={400} />
      </SidebarLayout>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SidebarLayout>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box data-tour="expense-header">
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Expense Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage your property expenses with detailed analytics and reporting.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <IconButton
              onClick={() => setHelpDialogOpen(true)}
              data-tour="help-button"
              title="Get help with expense management"
            >
              <HelpIcon />
            </IconButton>
            {!tourCompleted && (
              <IconButton
                onClick={startTour}
                color="primary"
                title="Take a guided tour"
              >
                <TourIcon />
              </IconButton>
            )}
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <Refresh />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Expense Notifications */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ExpenseNotifications
            onNotificationClick={(notification) => {
              // Handle notification click - could navigate to specific expense or open modal
              console.log('Notification clicked:', notification);
            }}
            onUnreadCountChange={(count) => {
              // Update any global notification count if needed
              console.log('Unread count changed:', count);
            }}
          />
        </Box>

        {/* Reminder Notifications */}
        {reminders.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            icon={<Notifications />}
            action={
              <Button color="inherit" size="small">
                View All ({reminders.length})
              </Button>
            }
          >
            You have {reminders.length} pending expense reminder{reminders.length > 1 ? 's' : ''} that need attention.
          </Alert>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Filter Expenses
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date) => handleFilterChange('startDate', date ? format(date, 'yyyy-MM-dd') : '')}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date) => handleFilterChange('endDate', date ? format(date, 'yyyy-MM-dd') : '')}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.categoryId || ''}
                    onChange={(e) => handleFilterChange('categoryId', e.target.value || undefined)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.recurrenceType || ''}
                    onChange={(e) => handleFilterChange('recurrenceType', e.target.value || undefined)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="one_time">One-time</MenuItem>
                    <MenuItem value="fixed_recurring">Fixed Recurring</MenuItem>
                    <MenuItem value="variable_recurring">Variable Recurring</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Total Expenses</Typography>
                  </Box>
                  <TrendingUp sx={{ color: 'success.main' }} />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {formatCurrency(summaryData?.currentMonth?.total || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All time
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">This Month</Typography>
                  </Box>
                  <TrendingUp sx={{ color: 'success.main' }} />
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {formatCurrency(summaryData?.currentMonth?.total || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current month
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Category sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Categories</Typography>
                  </Box>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {categories.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available categories
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Total Entries</Typography>
                  </Box>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {expenses.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expense records
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              aria-label="expense management tabs"
            >
              <Tab label="Expenses" />
              <Tab label="Notifications" />
            </Tabs>
          </Box>

          {currentTab === 0 && (
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Recent Expenses
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddExpense}
                  data-tour="add-expense-button"
                >
                  Add Expense
                </Button>
              </Box>

              {expenses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Receipt sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No expenses found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {showFilters ? 'Try adjusting your filters or add your first expense.' : 'Add your first expense to get started with expense tracking.'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddExpense}
                  >
                    Add Your First Expense
                  </Button>
                </Box>
              ) : (
                <Box>
                  {expenses.map((expense) => (
                    <Paper
                      key={expense.expenseId}
                      sx={{
                        p: 3,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(expense.amount, expense.currency)}
                            </Typography>
                            <Chip
                              label={getCategoryName(expense.categoryId)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={expense.recurrenceType.replace('_', ' ')}
                              size="small"
                              color={getRecurrenceTypeColor(expense.recurrenceType) as any}
                            />
                          </Box>

                          <Typography variant="body1" color="text.secondary" gutterBottom>
                            {expense.description || 'No description'}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(expense.date), 'MMM dd, yyyy')}
                            </Typography>
                            {expense.receiptUrl && (
                              <Chip
                                label="Receipt"
                                size="small"
                                icon={<Receipt />}
                                variant="outlined"
                              />
                            )}
                            {expense.isRecurringGenerated && (
                              <Chip
                                label="Auto-generated"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <IconButton
                          onClick={(e) => handleMenuOpen(e, expense.expenseId)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          )}

          {currentTab === 1 && (
            <CardContent>
              <ExpenseNotificationHistory />
            </CardContent>
          )}
        </Card>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add expense"
          onClick={handleAddExpense}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
        >
          <Badge badgeContent={reminders.length} color="error">
            <Add />
          </Badge>
        </Fab>

        {/* Expense Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && Boolean(selectedExpense)}
          onClose={handleMenuClose}
        >
          {selectedExpense && (
            <>
              <MenuItem onClick={() => {
                const expense = expenses.find(e => e.expenseId === selectedExpense)
                if (expense) handleEditExpense(expense)
              }}>
                <Edit sx={{ mr: 1 }} />
                Edit Expense
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>
                <Receipt sx={{ mr: 1 }} />
                View Receipt
              </MenuItem>
              <MenuItem
                onClick={() => handleDeleteExpense(selectedExpense)}
                sx={{ color: 'error.main' }}
              >
                <Delete sx={{ mr: 1 }} />
                Delete Expense
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Help Dialog */}
        <ExpenseHelpDialog
          open={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
        />

        {/* Feature Tour */}
        <ExpenseFeatureTour
          open={tourOpen}
          onClose={closeTour}
          onComplete={completeTour}
        />

        {/* Expense Form Dialog */}
        <Dialog
          open={expenseFormOpen}
          onClose={handleCancelExpenseForm}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <ExpenseForm
                onSave={handleSaveExpense}
                onCancel={handleCancelExpenseForm}
                initialData={editingExpense ? {
                  categoryId: editingExpense.categoryId,
                  amount: editingExpense.amount,
                  currency: editingExpense.currency,
                  description: editingExpense.description,
                  date: editingExpense.date,
                  receiptUrl: editingExpense.receiptUrl,
                  listingId: editingExpense.listingId,
                  recurrenceType: editingExpense.recurrenceType,
                  recurrenceConfig: editingExpense.recurrenceConfig,
                } : undefined}
                isLoading={savingExpense}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </SidebarLayout>
    </LocalizationProvider>
  )
}